import BaseService from './base-service';
import GraphQLService from './graphql-service';
import {GraphQlField, GraphQlSchema, GraphQlType} from '../types/GraphQlSchema';
import {ObjectDetails, ObjectInfo} from '../types/Object';
import {GraphQlQuery} from '../types/GraphQlQuery';
import {
  getLoadMoreObjectsQuery,
  getObjectDetailsQuery,
  getObjectTypeInfoQuery
} from './builders/object-details-query-builder';
import {mapDetailsResponse, mapInfoResponse, mapMoreObjectsResponse} from './mappers/object-details-mapper';

type BaseErrorResponse = {
  errors: Array<any>;
};

export type ObjectInfoResponse = BaseErrorResponse & {
  type: ObjectInfo;
};

export type ObjectDetailsResponse = BaseErrorResponse & {
  details: ObjectDetails;
};

export type MoreObjectsResponse = BaseErrorResponse & {
  objects: Array<any>
};

/**
 * Service for working with objects via the GraphQL API.
 */
export default class ObjectsService extends BaseService {

  /**
   * Loads the basic information about an object.
   *
   * @param {string} objectId the identifier of the object to retrieve the info fo
   * @returns {Promise<ObjectInfoResponse>}
   */
  public static loadInfo(objectId: string): Promise<ObjectInfoResponse> {
    const query = getObjectTypeInfoQuery(objectId);
    return this.executeQuery(query).then((res) => this.resolveTypeResponse(res));
  }

  /**
   * Loads detailed information about an object, based ont its GraphQL type and fields.
   *
   * @param {string} objectId the identifier of the object for which to retrieve details
   * @param {GraphQlType} type the GraphQL type of the object
   * @param {GraphQlSchema} graphQlSchema the GraphQL schema of the GraphQL type
   * @returns {Promise<ObjectDetailsResponse>}
   */
  public static loadDetails(objectId: string,
                            type: GraphQlType,
                            graphQlSchema: GraphQlSchema): Promise<ObjectDetailsResponse> {
    const query = getObjectDetailsQuery(objectId, type, graphQlSchema);
    return this.executeQuery(query).then((res) => this.resolveDetailsResponse(res, type));
  }

  /**
   * Loads additional object properties for specific object and object field.
   *
   * @param payload contains the required data for retrieving additional properties
   * @returns {Promise<MoreObjectsResponse>}
   */
  public static loadMoreObjects(payload: {
    objectId: string,
    type: GraphQlType,
    field: GraphQlField,
    limit: number,
    offset: number
  }): Promise<MoreObjectsResponse> {
    const query = getLoadMoreObjectsQuery(payload.objectId, payload.type, payload.field, payload.limit, payload.offset);
    return this.executeQuery(query).then((res) => this.resolveLoadMoreResponse(res, payload.field));
  }

  private static executeQuery(query: GraphQlQuery) {
    // Tell the backend to use the federation if available
    return GraphQLService.getQueryExecutor('federation')(query)
      // Handle HTTP 4xx/5xx on /graphql
      .catch(response => Promise.reject({
        errors: response?.data?.errors || []
      }));
  }

  private static resolveTypeResponse(response: any): Promise<ObjectInfoResponse> {
    if (this.hasResponseObject(response)) {
      return Promise.resolve({
        type: mapInfoResponse(response.data),
        errors: response.errors || []
      });
    }
    return Promise.reject({
      errors: response.errors || []
    });
  }

  private static resolveDetailsResponse(response: any, type: GraphQlType): Promise<ObjectDetailsResponse> {
    if (this.hasResponseObject(response)) {
      return Promise.resolve({
        details: mapDetailsResponse(response.data, type),
        errors: response.errors || []
      });
    }
    return Promise.reject({
      errors: response.errors || []
    });
  }

  private static resolveLoadMoreResponse(response: any, field: GraphQlField): Promise<MoreObjectsResponse> {
    if (this.hasResponseObject(response)) {
      return Promise.resolve({
        objects: mapMoreObjectsResponse(response.data, field),
        errors: response.errors || []
      });
    }
    return Promise.reject({
      errors: response.errors || []
    });
  }

  private static hasResponseObject(response: any): boolean {
    const data = response?.data;
    if (!data) {
      return false;
    }
    const responseObjects: any = Object.values(data)[0];
    return responseObjects && responseObjects[0] !== null;
  }
}
